import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, ArrowRight, Sliders } from "lucide-react";

/**
 * BudgetAlertsStrip — amber/red warning strip shown on the dashboard when any
 * project-month OR budget-vertical crosses (or nears) its Budget Plan cap.
 * Per-vertical warning levels are admin-configurable (Settings → Alert Thresholds).
 * Renders nothing when everything is within plan.
 */
export function BudgetAlertsStrip() {
  const { money } = useCurrency();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => { api.budgetAlerts().then(setData).catch(() => setData(null)); }, []);

  const projectAlerts = data?.alerts || [];
  const verticalAlerts = data?.vertical_alerts || [];
  if (!data || !data.plan_available || (projectAlerts.length === 0 && verticalAlerts.length === 0)) return null;

  const projOver = projectAlerts.filter((a) => a.severity === "over").length;
  const projNear = projectAlerts.filter((a) => a.severity === "near").length;
  const vOver = data.vertical_over_count || verticalAlerts.filter((a) => a.severity === "over").length;
  const vNear = data.vertical_near_count || verticalAlerts.filter((a) => a.severity === "near").length;

  const danger = projOver > 0 || vOver > 0;
  const toneCls = danger
    ? "border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.07)] text-[hsl(var(--danger))]"
    : "border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.08)] text-[hsl(var(--warning))]";

  const bits = [];
  if (vOver > 0) bits.push(`${vOver} vertical${vOver === 1 ? "" : "s"} over plan`);
  if (projOver > 0) bits.push(`${projOver} project-month${projOver === 1 ? "" : "s"} over plan`);
  const nearTotal = vNear + projNear;
  if (nearTotal > 0) bits.push(`${nearTotal} nearing the cap`);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={`rounded-lg border ${toneCls}`} data-testid="budget-alerts-strip">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left" data-testid="budget-alerts-toggle">
            <span className="flex items-center gap-2 text-sm font-semibold">
              <AlertTriangle className="h-4 w-4" /> Budget plan alerts
            </span>
            <span className="text-xs opacity-90" data-testid="budget-alerts-summary">{bits.join(" · ")}</span>
            <ChevronDown className={`ml-auto h-4 w-4 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3 border-t border-inherit px-4 py-2.5" data-testid="budget-alerts-list">
            {verticalAlerts.length > 0 && (
              <div>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">By vertical</p>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {verticalAlerts.slice(0, 8).map((a) => (
                    <div key={`${a.vertical}-${a.month}`}
                      className="flex items-center justify-between rounded-md border bg-card px-2.5 py-1.5 text-xs text-foreground"
                      data-testid={`budget-vertical-alert-${a.vertical.replace(/[^a-zA-Z0-9]+/g, "-")}-${a.month}`}>
                      <span className="font-medium">{a.vertical} · {a.month}</span>
                      <span className="num tabular-nums">
                        {money(a.spent)} / {money(a.cap)}
                        <b className={a.severity === "over" ? "ml-1.5 text-[hsl(var(--danger))]" : "ml-1.5 text-[hsl(var(--warning))]"}>{Math.round(a.ratio * 100)}%</b>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {projectAlerts.length > 0 && (
              <div>
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">By project-month</p>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {projectAlerts.slice(0, 8).map((a) => (
                    <div key={`${a.project}-${a.month}`}
                      className="flex items-center justify-between rounded-md border bg-card px-2.5 py-1.5 text-xs text-foreground"
                      data-testid={`budget-alert-${a.project.replace(/[^a-zA-Z0-9]+/g, "-")}-${a.month}`}>
                      <span className="font-medium">{a.project} · {a.month}</span>
                      <span className="num tabular-nums">
                        {money(a.spent)} / {money(a.cap)}
                        <b className={a.severity === "over" ? "ml-1.5 text-[hsl(var(--danger))]" : "ml-1.5 text-[hsl(var(--warning))]"}>{Math.round(a.ratio * 100)}%</b>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => navigate("/budget-plan")} data-testid="budget-alerts-open-plan">
                Open Budget Plan <ArrowRight className="h-3 w-3" />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => navigate("/settings")} data-testid="budget-alerts-thresholds">
                <Sliders className="h-3 w-3" /> Adjust thresholds
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}
